import React, { useState, useEffect, useRef } from 'react';
import { X, Eraser, Pen, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Modale de signature réutilisable (souris + tactile).
 * Props :
 *  - open : boolean
 *  - onClose : () => void
 *  - onSign : ({ nom, qualite, data_url }) => Promise
 *  - title : string
 *  - subtitle : string
 *  - defaultNom : string
 */
export default function SignaturePadModal({ open, onClose, onSign, title = 'Signer le document', subtitle, defaultNom = '' }) {
  const canvasRef = useRef(null);
  const [nom, setNom] = useState(defaultNom);
  const [qualite, setQualite] = useState('');
  const [accepte, setAccepte] = useState(false);
  const [hasSig, setHasSig] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { if (open) setNom(defaultNom || ''); }, [open, defaultNom]);

  useEffect(() => {
    if (!open) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#0f172a';
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    let drawing = false;

    const pos = (x, y) => {
      const r = canvas.getBoundingClientRect();
      return { x: (x - r.left) * (canvas.width / r.width), y: (y - r.top) * (canvas.height / r.height) };
    };
    const start = (x, y) => { drawing = true; const p = pos(x, y); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
    const move = (x, y) => { if (!drawing) return; const p = pos(x, y); ctx.lineTo(p.x, p.y); ctx.stroke(); setHasSig(true); };
    const end = () => { drawing = false; };

    const md = (e) => { e.preventDefault(); start(e.clientX, e.clientY); };
    const mm = (e) => { if (drawing) { e.preventDefault(); move(e.clientX, e.clientY); } };
    const mu = () => end();
    const ts = (e) => { if (!e.touches?.length) return; e.preventDefault(); const t = e.touches[0]; start(t.clientX, t.clientY); };
    const tm = (e) => { if (!drawing || !e.touches?.length) return; e.preventDefault(); const t = e.touches[0]; move(t.clientX, t.clientY); };
    const te = (e) => { e.preventDefault(); end(); };

    canvas.addEventListener('mousedown', md);
    canvas.addEventListener('mousemove', mm);
    canvas.addEventListener('mouseup', mu);
    canvas.addEventListener('mouseleave', mu);
    canvas.addEventListener('touchstart', ts, { passive: false });
    canvas.addEventListener('touchmove', tm, { passive: false });
    canvas.addEventListener('touchend', te, { passive: false });
    canvas.addEventListener('touchcancel', te, { passive: false });
    return () => {
      canvas.removeEventListener('mousedown', md);
      canvas.removeEventListener('mousemove', mm);
      canvas.removeEventListener('mouseup', mu);
      canvas.removeEventListener('mouseleave', mu);
      canvas.removeEventListener('touchstart', ts);
      canvas.removeEventListener('touchmove', tm);
      canvas.removeEventListener('touchend', te);
      canvas.removeEventListener('touchcancel', te);
    };
  }, [open]);

  const clear = () => {
    const c = canvasRef.current;
    if (!c) return;
    c.getContext('2d').clearRect(0, 0, c.width, c.height);
    setHasSig(false);
  };

  const submit = async () => {
    if (!nom.trim()) { toast.error('Indiquez votre nom'); return; }
    if (!accepte) { toast.error('Acceptez les conditions'); return; }
    if (!hasSig) { toast.error('Signez dans le cadre'); return; }
    setSubmitting(true);
    try {
      const data_url = canvasRef.current.toDataURL('image/png');
      await onSign({ nom, qualite, data_url });
    } catch (e) {
      toast.error(e?.message || 'Erreur de signature');
    } finally { setSubmitting(false); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6" data-testid="signature-pad-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[95vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gradient-to-r from-emerald-600 to-teal-600 rounded-t-2xl text-white">
          <div>
            <h2 className="text-lg font-bold flex items-center gap-2"><Pen className="w-5 h-5" /> {title}</h2>
            {subtitle && <p className="text-xs text-white/80 mt-0.5">{subtitle}</p>}
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/20" data-testid="signature-modal-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Votre nom *</label>
            <input value={nom} onChange={e => setNom(e.target.value)} placeholder="Prénom Nom"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
              data-testid="signature-input-nom" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Qualité (optionnel)</label>
            <input value={qualite} onChange={e => setQualite(e.target.value)} placeholder="Gérant, Exploitant, ..."
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
              data-testid="signature-input-qualite" />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-slate-600">Signature (doigt ou souris) *</label>
              <button type="button" onClick={clear}
                className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
                data-testid="signature-clear">
                <Eraser className="w-3 h-3" /> Effacer
              </button>
            </div>
            <div className="border-2 border-dashed border-emerald-400 rounded-xl bg-slate-50 touch-none select-none">
              <canvas ref={canvasRef} width={800} height={240}
                className="w-full h-[200px] sm:h-[240px] cursor-crosshair"
                data-testid="signature-canvas" />
            </div>
            {!hasSig && <p className="text-[11px] text-slate-400 mt-1 italic">Signez dans le cadre ci-dessus</p>}
          </div>

          <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
            <input type="checkbox" checked={accepte} onChange={e => setAccepte(e.target.checked)}
              className="mt-1 w-4 h-4 accent-emerald-600" data-testid="signature-accept" />
            <span>J'accepte les termes du document et je certifie que ma signature a valeur d'engagement.</span>
          </label>

          <div className="flex gap-3 pt-2">
            <button onClick={onClose}
              className="flex-1 px-4 py-3 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold transition-all"
              data-testid="signature-cancel">
              Annuler
            </button>
            <button onClick={submit} disabled={submitting}
              className="flex-1 px-4 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold shadow-lg transition-all inline-flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="signature-submit">
              {submitting ? 'Signature…' : (<><CheckCircle className="w-4 h-4" /> Valider la signature</>)}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
